import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import normalizeEmail from '@clearscore/normalize.email';
import requestStatuses from '@clearscore/constants.request-statuses';
import { resolveProp } from '@clearscore/helpers.resolve-props';
import { isRequiredValidation } from '@clearscore/validation.is-required';
import { isEmailValidation } from '@clearscore/validation.is-email';
import FormLayout from '@clearscore/rainbow.form-layout';
import Text from '@clearscore/rainbow.text';
import Button from '@clearscore/rainbow.button';
import Label from '@clearscore/rainbow.label';
import TextInput from '@clearscore/rainbow.text-input';

const ResetPasswordForm = ({ name, handleReset, language }) => {
    // TODO: Migrate to use selector created with @clearscore/redux.create-requests
    const isResetPending =
        useSelector((state) => resolveProp('resetPassword.status', state)) === requestStatuses.PENDING;

    return (
        <Form
            name={name}
            onSubmit={handleReset}
            render={({ handleSubmit, pristine, invalid }) => (
                <form onSubmit={handleSubmit}>
                    <FormLayout>
                        <FormLayout.Fieldset>
                            <FormLayout.Item>
                                <Label htmlFor="email">{language.email.label}</Label>
                                <Field
                                    name="email"
                                    type="email"
                                    parse={normalizeEmail}
                                    validate={(data) => isRequiredValidation()(data) || isEmailValidation()(data)}
                                >
                                    {({ input, meta }) => (
                                        <TextInput
                                            id="email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            value={input.value}
                                            onBlur={input.onBlur}
                                            onChange={input.onChange}
                                            isValid={meta.touched && meta.valid}
                                            isInvalid={meta.touched && meta.invalid}
                                            placeholder={language.email.placeholder}
                                            errorMessage={(meta.touched && meta.error) || ''}
                                        />
                                    )}
                                </Field>
                            </FormLayout.Item>
                            <FormLayout.Item>
                                <Button
                                    htmlType="submit"
                                    isNarrow
                                    isResponsive
                                    type={Button.types.PRIMARY}
                                    size={Button.sizes.LARGE}
                                    isLoading={isResetPending}
                                    isDisabled={pristine || isResetPending || invalid}
                                >
                                    {language.reset}
                                </Button>
                            </FormLayout.Item>
                            <FormLayout.Item>
                                <Text.Link1 dataId="already-know-password-link" href="/login">
                                    {language.alreadyKnowPassword}
                                </Text.Link1>
                            </FormLayout.Item>
                        </FormLayout.Fieldset>
                    </FormLayout>
                </form>
            )}
        />
    );
};

ResetPasswordForm.propTypes = {
    /** @type {func} callback for the data */
    handleReset: PropTypes.func.isRequired,
    /** @type {object} language object */
    language: PropTypes.shape({
        alreadyKnowPassword: PropTypes.string.isRequired,
        reset: PropTypes.string.isRequired,
        email: PropTypes.shape({
            label: PropTypes.string.isRequired,
            placeholder: PropTypes.string.isRequired,
        }),
    }).isRequired,
    /** @type {string} redux form name */
    name: PropTypes.string,
};

ResetPasswordForm.defaultProps = {
    name: 'ResetPasswordForm',
};

export default ResetPasswordForm;
